/**
 * SiteIntelSection — home-page teaser beneath the Explore Area block.
 * Lets the user scan any parcel for HT lines / water / wetlands + get an
 * AI verdict, without navigating away. Full scanner lives at /site-intel.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanLine, Zap, Waves, Trees, LocateFixed, ArrowRight, MapPin, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const parseLatLng = (raw) => {
  if (!raw) return null;
  const s = raw.trim();
  let m = s.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  m = s.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  m = s.match(/^\s*(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)\s*$/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  return null;
};

export const SiteIntelSection = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');

  const openScanner = (queryStr = '') => {
    const q = queryStr && `?q=${encodeURIComponent(queryStr)}`;
    navigate(`/site-intel${q}`);
  };

  const goWithInput = () => {
    const parsed = parseLatLng(input);
    if (!parsed) { openScanner(input); return; }   // still route, the page will re-parse
    openScanner(input || `${parsed.lat},${parsed.lng}`);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return openScanner('');
    navigator.geolocation.getCurrentPosition(
      (pos) => openScanner(`${pos.coords.latitude},${pos.coords.longitude}`),
      () => openScanner(''),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <section id="site-intel" className="py-16 sm:py-20 bg-slate-950 text-white overflow-hidden relative" data-testid="site-intel-section">
      {/* Ambient grid backdrop */}
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(rgba(16,185,129,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.15) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      <div className="max-w-6xl mx-auto px-4 relative">
        <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-black uppercase tracking-widest">
          <ScanLine size={14} /> Site intel · beta
        </div>
        <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black">
          Scan any plot in <span className="text-emerald-400">under 30 seconds</span>.
        </h2>
        <p className="mt-3 text-slate-300 max-w-2xl text-sm sm:text-base">
          Paste a Google Maps link, type coordinates, or use your current location. We'll pull HT lines, water bodies and wetlands within up to <b>100 km</b> and hand you an AI verdict: farming, infrastructure, or mixed use.
        </p>

        {/* Feature chips */}
        <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4 max-w-lg">
          {[
            [Zap,   'HT lines',   'red-400'],
            [Waves, 'Water bodies', 'sky-400'],
            [Trees, 'Wetlands',   'emerald-400'],
          ].map(([Icon, label, color]) => (
            <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/60 backdrop-blur px-3 py-3 text-center">
              <Icon size={18} className={`mx-auto mb-1 text-${color}`} />
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</div>
            </div>
          ))}
        </div>

        {/* Input row */}
        <div className="mt-8 rounded-3xl bg-white/5 backdrop-blur border border-slate-800 p-4 sm:p-6 max-w-3xl">
          <label className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Enter a location</label>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') goWithInput(); }}
                placeholder="22.5726, 88.3639  OR  https://maps.google.com/?q=..."
                className="pl-9 bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="site-intel-home-input"
              />
            </div>
            <Button onClick={goWithInput} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="site-intel-home-go">
              <ScanLine size={14} className="mr-1" /> Scan
            </Button>
            <Button variant="outline" onClick={useMyLocation} className="border-slate-600 bg-slate-900/60 text-slate-200 hover:bg-slate-800" data-testid="site-intel-home-locate">
              <LocateFixed size={14} className="mr-1" /> My location
            </Button>
          </div>
          <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-400">
            <Sparkles size={11} className="text-emerald-400 mt-0.5 shrink-0" />
            <span>Rule-based AI verdict — deterministic, source-cited (OSM). Free, no signup required.</span>
          </div>
        </div>

        {/* Big CTA */}
        <div className="mt-6">
          <Button onClick={() => openScanner('')} variant="ghost" className="text-emerald-300 hover:text-emerald-200 hover:bg-slate-900/40" data-testid="site-intel-home-explore">
            Or open the full-screen scanner <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SiteIntelSection;
